/**
 * Tenant Certificate Service
 * Handles API requests related to tenant certificate management
 */
import { createService } from './serviceFactory';

// Define the base URL for tenant certificate service
const baseUrl = '/api/supplier/tenant-certificates';

// Create base service with standard CRUD operations
const baseService = createService(baseUrl);

const tenantCertificateService = {
  // Include all methods from the base service
  ...baseService,
  
  /**
   * Get all tenant certificates
   * @returns {Promise} - Response with certificates list
   */
  getTenantCertificates() {
    return baseService.client.get(`${baseUrl}`)
      .then(response => response.data);
  },

  /**
   * Get tenant certificate by ID
   * @param {string} id - Certificate ID
   * @returns {Promise} - Response with certificate details
   */
  getTenantCertificateById(id) {
    if (!id) {
      console.error('getTenantCertificateById: id is required');
      return Promise.reject(new Error('Certificate ID is required'));
    }
    
    return baseService.client.get(`${baseUrl}/${id}`)
      .then(response => response.data);
  },

  /**
   * Create new tenant certificate
   * @param {Object} certificateData - Certificate data
   * @returns {Promise} - Response with created certificate
   */
  createTenantCertificate(certificateData) {
    if (!certificateData) {
      console.error('createTenantCertificate: certificateData is required');
      return Promise.reject(new Error('Certificate data is required'));
    }
    
    return baseService.client.post(`${baseUrl}`, certificateData)
      .then(response => response.data);
  },

  /**
   * Update tenant certificate
   * @param {string} id - Certificate ID
   * @param {Object} certificateData - Certificate data
   * @returns {Promise} - Response with updated certificate
   */
  updateTenantCertificate(id, certificateData) {
    if (!id) {
      console.error('updateTenantCertificate: id is required');
      return Promise.reject(new Error('Certificate ID is required'));
    }
    
    if (!certificateData) {
      console.error('updateTenantCertificate: certificateData is required');
      return Promise.reject(new Error('Certificate data is required'));
    }
    
    return baseService.client.put(`${baseUrl}/${id}`, certificateData)
      .then(response => response.data);
  },

  /**
   * Delete tenant certificate
   * @param {string} id - Certificate ID
   * @returns {Promise} - Response confirmation
   */
  deleteTenantCertificate(id) {
    if (!id) {
      console.error('deleteTenantCertificate: id is required');
      return Promise.reject(new Error('Certificate ID is required'));
    }
    
    return baseService.client.delete(`${baseUrl}/${id}`)
      .then(response => response.data);
  },

  /**
   * Search tenant certificates with pagination
   * @param {Object} searchParams - Search parameters
   * @returns {Promise} - Response with search results
   */
  searchTenantCertificates(searchParams = {}) {
    const defaultParams = {
      searchText: '',
      page: 0,
      pageSize: 20,
      sorting: '',
      certificateTypeId: '',
      certificateName: '',
      expirationDateFrom: '',
      expirationDateTo: '',
      isExpired: null
    };
    
    const params = { ...defaultParams, ...searchParams };
    
    return baseService.client.post(`${baseUrl}/search`, params)
      .then(response => response.data);
  },

  /**
   * Get expiring certificates
   * @returns {Promise} - Response with expiring certificates
   */
  getExpiringCertificates() {
    return baseService.client.get(`${baseUrl}/expiring-certificates`)
      .then(response => response.data);
  }
};

export default tenantCertificateService; 